import {
  clientLocationSummaryPlanBreakdownDateRangeOrder,
  type ClientLocationSummaryPlanBreakdownDateRange,
} from "api-types/locations/locationsResponses";
import { locationHomePlansPath, locationPlanListPath } from "lib/routeHelpers";
import { useState, type ReactNode } from "react";
import Link from "ui/components/Link";
import ToggleGroup from "ui/forms/ToggleGroup";
import { formatCount } from "./locationSummaryFormatting";
import { planBreakdownRangeLabels } from "./locationSummaryLabels";
import type { ClientLocationPlanBreakdownByRange, LocationSummaryScope } from "./locationSummaryTypes";
import styles from "./LocationSummaryView.module.scss";
import { derivePlanBreakdownView, type PlanBreakdownDisplayRow } from "./planBreakdownRows";

export default function PlansOnHomesCard({
  data,
  totalCommunityPlans,
  isLeaf,
  scope,
}: {
  data: ClientLocationPlanBreakdownByRange;
  totalCommunityPlans: number;
  isLeaf: boolean;
  scope: LocationSummaryScope;
}): ReactNode {
  const [range, setRange] = useState<ClientLocationSummaryPlanBreakdownDateRange>("ALL");
  const [showAllRows, setShowAllRows] = useState(false);
  const breakdown = data[range];
  const canAddPlans = isLeaf && scope.kind === "location";
  const breakdownView = derivePlanBreakdownView(breakdown, showAllRows);

  return (
    <section className={styles.card} data-testid="plans-on-homes">
      <div className={styles.cardHeaderRow}>
        <div className={styles.cardHeader}>
          <h3>Plans on Homes</h3>
          <p>
            {formatCount(breakdown.totalHomes)} total homes across {formatCount(breakdown.totalPlanTypes)}{" "}
            {breakdown.totalPlanTypes === 1 ? "plan" : "plans"}
          </p>
        </div>
        <ToggleGroup.Root
          type="single"
          value={range}
          onValueChange={(value: string) => {
            if (value) {
              setRange(value as ClientLocationSummaryPlanBreakdownDateRange);
              setShowAllRows(false);
            }
          }}
          allowEmpty={false}
          toggleStyle="secondary"
          className={styles.rangeToggle}
          aria-label="Plan breakdown date range"
        >
          {clientLocationSummaryPlanBreakdownDateRangeOrder.map((dateRange) => (
            <ToggleGroup.Item value={dateRange} key={dateRange}>
              {planBreakdownRangeLabels[dateRange]}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>
      </div>
      {breakdown.planBreakdownRows.length > 0 ? (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Plan Name</th>
                  <th scope="col">Count</th>
                  <th scope="col">%</th>
                </tr>
              </thead>
              <tbody>
                {breakdownView.rankedRows.map((row) => (
                  <PlanRow key={row.planName} row={row} />
                ))}
                {breakdownView.otherRow && (
                  <tr className={styles.mutedRow}>
                    <th scope="row">
                      Other ({formatCount(breakdownView.otherRow.planCount)}{" "}
                      {breakdownView.otherRow.planCount === 1 ? "plan" : "plans"})
                    </th>
                    <td>{formatCount(breakdownView.otherRow.count)}</td>
                    <td>{Math.round(breakdownView.otherRow.percentage)}%</td>
                  </tr>
                )}
                {breakdownView.unusedRows.map((row) => (
                  <PlanRow key={row.planName} row={row} />
                ))}
                <tr className={styles.totalRow}>
                  <th scope="row">Total</th>
                  <td>{formatCount(breakdown.totalHomes)}</td>
                  <td>{breakdown.totalHomes === 0 ? "--" : "100%"}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {breakdownView.expanderLabel && (
            <button
              type="button"
              className={styles.textButton}
              aria-expanded={showAllRows}
              onClick={() => setShowAllRows(!showAllRows)}
            >
              {breakdownView.expanderLabel}
            </button>
          )}
        </>
      ) : (
        <PlansOnHomesEmptyState
          totalCommunityPlans={totalCommunityPlans}
          hasAnyHomePlans={data.ALL.totalHomes > 0}
          canAddPlans={canAddPlans}
          scope={scope}
        />
      )}
    </section>
  );
}

function PlanRow({ row }: { row: PlanBreakdownDisplayRow }): ReactNode {
  return (
    <tr className={row.isUnused ? styles.mutedRow : undefined}>
      <th scope="row">{row.planName}</th>
      <td data-zero={row.count === 0 || undefined}>{formatCount(row.count)}</td>
      <td data-zero={row.count === 0 || undefined}>{Math.round(row.percentage)}%</td>
    </tr>
  );
}

function PlansOnHomesEmptyState({
  totalCommunityPlans,
  hasAnyHomePlans,
  canAddPlans,
  scope,
}: {
  totalCommunityPlans: number;
  hasAnyHomePlans: boolean;
  canAddPlans: boolean;
  scope: LocationSummaryScope;
}): ReactNode {
  const needsCommunityPlans = totalCommunityPlans === 0;
  let title = "No homes in this date range";
  let message = "Choose another date range to see plans and homes breakdown.";

  if (!hasAnyHomePlans && needsCommunityPlans) {
    title = "No community plans yet";
    message = "Add community plans to see plans and homes breakdown.";
  } else if (!hasAnyHomePlans) {
    title = "No home plans yet";
    message = "Add home plans to see plans and homes breakdown.";
  }

  const showAddPlanCta = canAddPlans && !hasAnyHomePlans;

  return (
    <div className={styles.emptyState}>
      <strong>{title}</strong>
      <p>{message}</p>
      {showAddPlanCta && scope.kind === "location" && (
        <Link
          href={
            needsCommunityPlans
              ? locationPlanListPath(scope.builderId, scope.locationId)
              : locationHomePlansPath(scope.builderId, scope.locationId)
          }
          className={styles.textLink}
        >
          {needsCommunityPlans ? "+ Add Community Plan" : "+ Add Home Plan"}
        </Link>
      )}
    </div>
  );
}
